import Splitting from 'splitting';

import {
  clamp,
  getCurrentTrailSlotIndex,
  getDominantSlotIndex,
  getElementLayoutSize,
  getExpertiseMediaAssetKey,
  getFrameEase,
  getLocalClipPolygon,
  getMediaRotation,
  getMediaRotationEase,
  getNextGalleryIndex,
  getRowBoundedViewportTarget,
  getStaggeredAutoplayDelay,
  getReusableTrailSlotIndex,
  getRotatedRectCorners,
  getSvgPolygonPoints,
  getViewportTarget,
  getWrappedGalleryIndex,
  isRectOutsideViewport,
  promoteTrailSlot,
  shouldSpawnExpertiseTrail,
  shouldPreventExpertisePointerFocus,
} from './expertise-motion.utils.js';

const initializedSections = new WeakSet();
let expertiseSectionId = 0;
const touchBreakpoint = 1033;
const viewportSafePadding = 24;
const mediaOddCursorBias = 0.84;
const mediaEvenCursorBias = 0.16;
const mediaVerticalCursorBias = 0.48;
const mediaBaseRotation = 4;
const mediaFlattenVelocity = 1600;
const mediaFlattenAmount = 0.95;
const mediaFollowEase = 0.105;
const mediaFlattenEase = 0.28;
const mediaRotationEase = 0.08;
const mediaVelocityDecayEase = 0.12;
const mediaSwitchOutDuration = 0.38;
const mediaSwitchInDuration = 0.46;
const mediaHideDuration = 0.24;
const mediaReadyTimeout = 1800;
const mediaVideoStartDelay = 120;
const posterFadeRemovalDelay = 320;
const motionPositionEpsilon = 0.08;
const motionRotationEpsilon = 0.015;
const maxCharStagger = 0.18;
const hoverLeaveDelay = 80;
const expertiseTrailPoolSize = 6;
const expertiseTrailDistance = 100;
const expertiseTrailDelay = 75;
const expertiseTrailInDuration = 0.55;
const expertiseTrailOutDuration = 0.5;
const expertiseTrailOverlayDuration = 0.22;
const expertiseTrailVerticalRange = 24;
const expertiseMobileAutoplayDelay = 3000;
const expertiseMobileAutoplayStagger = 450;
const expertiseMobileFadeDuration = 460;
const reducedMotionMediaQuery = '(prefers-reduced-motion: reduce)';
const touchPointerMediaQuery = '(hover: none), (pointer: coarse)';
const staticMobileMediaQuery = '(max-width: 640px)';

const getGsap = () => globalThis.gsap;

const getSections = (scope) => {
  if (!scope?.querySelectorAll) {
    return [];
  }

  if (scope.matches?.('.expertise-sec')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll('.expertise-sec'));
};

const toClientPoint = (point) => {
  if (!point) {
    return null;
  }

  return {
    x: point.clientX,
    y: point.clientY,
  };
};

const getTitlePoint = (title) => {
  const rect = title.getBoundingClientRect();

  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
};

const getInteractionPoint = (event, title) => {
  if (event?.touches?.[0]) {
    return toClientPoint(event.touches[0]);
  }

  if (event?.changedTouches?.[0]) {
    return toClientPoint(event.changedTouches[0]);
  }

  if (typeof event?.clientX === 'number' && typeof event?.clientY === 'number') {
    return toClientPoint(event);
  }

  return getTitlePoint(title);
};

const normalizeGalleryAsset = (dataset = {}) => {
  const src = dataset.src || dataset.expertiseImgSrc || '';
  const webp = dataset.webp || dataset.expertiseImgWebp || '';
  const poster = dataset.poster || dataset.expertisePosterSrc || '';
  const overlay = dataset.overlay === 'true' || dataset.expertiseOverlay === 'true';

  if (!src && !webp) {
    return null;
  }

  return { src, webp, poster, overlay };
};

const getRowGalleryAssets = (row) => {
  const firstAsset = normalizeGalleryAsset(row.dataset);
  const template = row.querySelector('template[data-expertise-gallery]');
  const additionalAssets = template
    ? Array.from(
      template.content.querySelectorAll('[data-expertise-gallery-item]'),
      (item) => normalizeGalleryAsset(item.dataset)
    ).filter(Boolean)
    : [];

  return firstAsset ? [firstAsset, ...additionalAssets] : additionalAssets;
};

const waitForImage = (image, timeout = mediaReadyTimeout, signal = null) =>
  new Promise((resolve) => {
    let settled = false;
    let timer = null;
    let handleAbort = null;

    const finish = (ready) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timer);
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleError);
      signal?.removeEventListener('abort', handleAbort);
      resolve(ready);
    };
    const handleLoad = () => finish(image.naturalWidth > 0);
    const handleError = () => finish(false);

    handleAbort = () => finish(false);

    if (signal?.aborted) {
      finish(false);
      return;
    }

    image.addEventListener('load', handleLoad, { once: true });
    image.addEventListener('error', handleError, { once: true });
    signal?.addEventListener('abort', handleAbort, { once: true });
    timer = window.setTimeout(
      () => finish(image.complete && image.naturalWidth > 0),
      timeout
    );

    if (image.complete) {
      handleLoad();
      return;
    }

    if (typeof image.decode === 'function') {
      image.decode().then(handleLoad).catch(() => {
        if (image.complete) {
          handleLoad();
        }
      });
    }
  });

const waitForVideo = (video, timeout = mediaReadyTimeout, signal = null) =>
  new Promise((resolve) => {
    let settled = false;
    let timer = null;
    let handleAbort = null;

    const finish = (ready) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timer);
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('error', handleError);
      signal?.removeEventListener('abort', handleAbort);
      resolve(ready);
    };
    const handleReady = () => finish(video.readyState >= 2);
    const handleError = () => finish(false);

    handleAbort = () => finish(false);

    if (signal?.aborted) {
      finish(false);
      return;
    }

    if (video.readyState >= 2) {
      finish(true);
      return;
    }

    video.addEventListener('loadeddata', handleReady, { once: true });
    video.addEventListener('error', handleError, { once: true });
    signal?.addEventListener('abort', handleAbort, { once: true });
    timer = window.setTimeout(() => finish(video.readyState >= 2), timeout);
  });

const waitForFirstReady = (promises) =>
  new Promise((resolve) => {
    let pending = promises.length;

    if (!pending) {
      resolve(false);
      return;
    }

    promises.forEach((promise) => {
      promise.then((ready) => {
        if (ready) {
          resolve(true);
          return;
        }

        pending -= 1;

        if (!pending) {
          resolve(false);
        }
      });
    });
  });

const watchImageReady = (image, onReady) => {
  let active = true;

  const cleanup = () => {
    if (!active) {
      return;
    }

    active = false;
    image.removeEventListener('load', handleLoad);
    image.removeEventListener('error', cleanup);
  };
  const handleLoad = () => {
    if (image.naturalWidth <= 0) {
      cleanup();
      return;
    }

    cleanup();
    onReady();
  };

  image.addEventListener('load', handleLoad, { once: true });
  image.addEventListener('error', cleanup, { once: true });

  if (image.complete) {
    handleLoad();
  }

  return cleanup;
};

const retireExpertisePoster = (host, poster) => {
  if (!poster || poster.dataset.expertisePosterRetiring === 'true') {
    return () => {};
  }

  poster.dataset.expertisePosterRetiring = 'true';
  host?.classList.add('is-media-ready');

  let timer = null;

  const removePoster = () => {
    window.clearTimeout(timer);
    poster.removeEventListener('transitionend', handleTransitionEnd);
    poster.removeAttribute('src');
    poster.remove();
  };
  const handleTransitionEnd = (event) => {
    if (event.target === poster && event.propertyName === 'opacity') {
      removePoster();
    }
  };

  poster.addEventListener('transitionend', handleTransitionEnd);
  poster.classList.add('is-retiring');
  timer = window.setTimeout(removePoster, posterFadeRemovalDelay);

  return removePoster;
};

const addMediaQueryListener = (mediaQuery, listener) => {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', listener);
    return;
  }

  mediaQuery.addListener?.(listener);
};

const initExpertiseSection = (scope = document) => {
  getSections(scope).forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const rows = Array.from(section.querySelectorAll('[data-expertise-row]'));
    const titles = rows.map((row) => row.querySelector('[data-expertise-title]'));
    const rowsContainer = section.querySelector('.expertise-sec__rows');
    const mediaLayer = section.querySelector('[data-expertise-media-layer]');
    const mediaInner = mediaLayer?.querySelector('.expertise-sec__media-inner');
    const mobileMedia = Array.from(
      section.querySelectorAll('[data-expertise-mobile-media]')
    );

    if (
      !rows.length ||
      titles.some((title) => !title) ||
      !mediaLayer ||
      !mediaInner
    ) {
      return;
    }

    initializedSections.add(section);

    const gsapInstance = getGsap();
    const touchMediaQuery = window.matchMedia(touchPointerMediaQuery);
    const reducedMotionQuery = window.matchMedia(reducedMotionMediaQuery);
    const staticMobileQuery = window.matchMedia(staticMobileMediaQuery);

    section.classList.add('is-expertise-enhanced');
    section.classList.toggle('has-expertise-gsap', !!gsapInstance);
    section.classList.toggle('is-reduced-motion', reducedMotionQuery.matches);

    titles.forEach((title) => {
      title.setAttribute('aria-pressed', 'false');

      if (title.classList.contains('is-expertise-splitting-init')) {
        return;
      }

      title.classList.add('is-expertise-splitting-init');
      Splitting({ target: title, by: 'chars' });

      const chars = Array.from(title.querySelectorAll('.char'));
      const charStep = chars.length > 1
        ? Math.min(0.015, maxCharStagger / (chars.length - 1))
        : 0;

      title.style.setProperty('--expertise-char-step', `${charStep}s`);
      chars.forEach((char) => {
        if (!char.hasAttribute('data-char')) {
          char.setAttribute('data-char', char.textContent);
        }
      });
    });

    let contrastLayer = rowsContainer?.querySelector('[data-expertise-contrast-layer]') || null;

    if (rowsContainer && !contrastLayer) {
      contrastLayer = document.createElement('div');
      contrastLayer.dataset.expertiseContrastLayer = '';
      contrastLayer.className = 'expertise-sec__contrast-layer';
      rowsContainer.append(contrastLayer);
    }

    const contrastTitles = contrastLayer
      ? titles.map((title, index) => {
        const contrastTitle = document.createElement('div');

        contrastTitle.className = `${title.className} expertise-sec__contrast-title`;
        contrastTitle.dataset.expertiseContrastTitle = `${index}`;
        contrastTitle.style.cssText = title.style.cssText;
        contrastTitle.classList.add('is-flip-idle');
        contrastTitle.replaceChildren(
          ...Array.from(title.childNodes, (node) => node.cloneNode(true))
        );

        return contrastTitle;
      })
      : [];

    if (contrastLayer) {
      contrastLayer.setAttribute('aria-hidden', 'true');
      contrastLayer.replaceChildren(...contrastTitles);
    }

    const rowGalleries = new Map(
      rows.map((row) => [row, getRowGalleryAssets(row)])
    );
    const rowGalleryIndexes = new Map();
    const rowHoverTokens = new Map();
    const imageCache = new Map();
    const svgNamespace = 'http://www.w3.org/2000/svg';
    const trailClipId = `expertise-trail-clip-${expertiseSectionId += 1}`;
    const trailSvg = document.createElementNS(svgNamespace, 'svg');
    const trailDefs = document.createElementNS(svgNamespace, 'defs');
    const trailClipPath = document.createElementNS(svgNamespace, 'clipPath');
    const trailLayer = document.createElement('div');

    trailSvg.classList.add('expertise-sec__trail-svg');
    trailSvg.setAttribute('aria-hidden', 'true');
    trailSvg.setAttribute('width', '0');
    trailSvg.setAttribute('height', '0');
    trailClipPath.id = trailClipId;
    trailClipPath.setAttribute('clipPathUnits', 'userSpaceOnUse');
    trailDefs.append(trailClipPath);
    trailSvg.append(trailDefs);
    section.append(trailSvg);

    trailLayer.className = 'expertise-sec__trail-layer';
    trailLayer.dataset.expertiseTrailLayer = '';
    trailLayer.setAttribute('aria-hidden', 'true');
    mediaLayer.parentElement.append(trailLayer);

    const trailSlots = Array.from({ length: expertiseTrailPoolSize }, () => {
      const element = document.createElement('div');
      const image = document.createElement('img');
      const overlay = document.createElement('div');
      const polygon = document.createElementNS(svgNamespace, 'polygon');

      element.className = 'expertise-sec__trail-item';
      element.dataset.expertiseTrailSlot = '';
      image.alt = '';
      image.decoding = 'async';
      overlay.className = 'expertise-sec__trail-overlay';
      element.append(image, overlay);
      trailLayer.append(element);
      trailClipPath.append(polygon);

      return {
        active: false,
        state: 'idle',
        el: element,
        image,
        overlay,
        polygon,
        timeline: null,
        startedAt: 0,
        row: null,
        point: null,
        x: 0,
        y: 0,
        rotation: 0,
      };
    });

    mediaInner.replaceChildren();

    const createMediaSlot = () => {
      const slotElement = document.createElement('div');

      slotElement.className = 'expertise-sec__media-slot';
      mediaInner.append(slotElement);

      return {
        el: slotElement,
        key: '',
        video: null,
        status: 'empty',
        loadToken: 0,
        loadController: null,
        dispose: null,
        playTimer: null,
        readyPromise: Promise.resolve(false),
      };
    };
    const slots = [];

    const setLayerX = gsapInstance?.quickSetter(mediaLayer, 'x', 'px') || null;
    const setLayerY = gsapInstance?.quickSetter(mediaLayer, 'y', 'px') || null;
    const setLayerRotation = gsapInstance?.quickSetter(mediaLayer, 'rotation', 'deg') || null;

    let activeRow = null;
    let activeTitle = null;
    let displayedRow = null;
    let displayedTitle = null;
    let displayedContrastTitle = null;
    let activeSlotIndex = -1;
    let interactionMode = null;
    let layerVisible = false;
    let tickerActive = false;
    let rafFrame = null;
    let resizeFrame = null;
    let contrastPositionFrame = null;
    let sectionVisibilityFrame = null;
    let mobileMediaObserver = null;
    let mobileVisibilityObserver = null;
    let sectionPreloadObserver = null;
    let hideTimer = null;
    let pauseTimer = null;
    let hoverLeaveTimer = null;
    let transitionTimeline = null;
    let mediaRequestToken = 0;
    let pointerFocusTitle = null;
    let pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    let pointerVelocityX = 0;
    let lastPointerMoveTime = 0;
    let target = { x: 0, y: 0, rotation: 0 };
    let rendered = { x: 0, y: 0, rotation: 0 };
    let layerSize = { width: 0, height: 0 };
    let lastFrameTime = 0;
    let lastAppliedTransform = { x: NaN, y: NaN, rotation: NaN };
    let lastAppliedClipPath = '';
    let trailActiveRow = null;
    let trailLastPoint = null;
    let trailLastTime = 0;
    let trailGalleryIndex = 0;
    let trailStackIndex = 0;
    let trailMaskFrame = null;
    let trailPositionFrame = null;
    let trailActivationToken = 0;
    let trailSize = { width: 0, height: 0 };

    const isTouchMode = () => window.innerWidth <= touchBreakpoint || touchMediaQuery.matches;
    const isReducedMotion = () => reducedMotionQuery.matches;
    const supportsSvgTrail = !!(
      contrastLayer &&
      globalThis.CSS?.supports?.('clip-path', `url(#${trailClipId})`)
    );
    const canUseTrail = () => (
      !!gsapInstance &&
      supportsSvgTrail &&
      !isTouchMode() &&
      !isReducedMotion()
    );
    const hasRowMedia = (row) => !!(row.dataset.expertiseVideoSrc || row.dataset.expertiseImgSrc);
    const getRowIndex = (row) => rows.indexOf(row);
    const getMediaSide = (row) => ((getRowIndex(row) + 1) % 2 === 1 ? 'left' : 'right');
    const getMediaKey = (row, asset = null) => {
      if (asset) {
        return [
          row.dataset.expertiseMediaType || 'image',
          asset.webp || '',
          asset.src || '',
          asset.overlay ? 'overlay' : '',
        ].join('|');
      }

      return `${getExpertiseMediaAssetKey(row.dataset)}|${row.dataset.expertiseOverlay || ''}`;
    };

    const loadGalleryAsset = (asset) => {
      if (!asset) {
        return Promise.resolve(null);
      }

      const key = asset.webp || asset.src;

      if (!key) {
        return Promise.resolve(null);
      }

      const cached = imageCache.get(key);

      if (cached) {
        return cached.promise;
      }

      const record = {
        status: 'loading',
        url: '',
        promise: null,
      };
      const loadCandidate = (url) => {
        if (!url) {
          return Promise.resolve(null);
        }

        const image = new Image();

        image.alt = '';
        image.decoding = 'async';
        image.src = url;

        return waitForImage(image, 5000).then((ready) => (ready ? url : null));
      };

      record.promise = loadCandidate(asset.webp)
        .then((url) => url || loadCandidate(asset.src))
        .then((url) => {
          record.status = url ? 'ready' : 'error';
          record.url = url || '';

          return url ? { ...asset, resolvedSrc: url } : null;
        });
      imageCache.set(key, record);

      return record.promise;
    };

    const preloadRowGallery = (row, all = false) => {
      const gallery = rowGalleries.get(row) || [];
      const assets = all ? gallery : gallery.slice(0, 1);

      return Promise.all(assets.map(loadGalleryAsset));
    };

    const getNextRowGalleryAsset = async (row) => {
      const gallery = rowGalleries.get(row) || [];

      if (!gallery.length) {
        return null;
      }

      const currentIndex = rowGalleryIndexes.get(row) ?? -1;

      for (let offset = 0; offset < gallery.length; offset += 1) {
        const index = getNextGalleryIndex(currentIndex + offset, gallery.length);
        const asset = await loadGalleryAsset(gallery[index]);

        if (asset) {
          rowGalleryIndexes.set(row, index);
          return asset;
        }
      }

      return null;
    };

    const getReadyGalleryAsset = (gallery, preferredIndex) => {
      if (!gallery.length) {
        return null;
      }

      for (let offset = 0; offset < gallery.length; offset += 1) {
        const index = getWrappedGalleryIndex(preferredIndex + offset, gallery.length);
        const asset = gallery[index];
        const record = imageCache.get(asset.webp || asset.src);

        if (record?.status === 'ready' && record.url) {
          return { asset: { ...asset, resolvedSrc: record.url }, index };
        }
      }

      return null;
    };

    const mobileStates = mobileMedia.map((media, index) => {
      const rowIndex = Number(media.dataset.expertiseMobileRowIndex);
      const row = rows[Number.isInteger(rowIndex) ? rowIndex : index] || null;

      return {
        index,
        media,
        row,
        track: media.querySelector('[data-expertise-mobile-track]'),
        gallery: row ? rowGalleries.get(row) || [] : [],
        slots: [],
        activeSlotIndex: 0,
        currentIndex: 0,
        timer: null,
        transitionToken: 0,
        hydrated: false,
        visible: false,
      };
    });

    const stopMobileAutoplay = (state) => {
      window.clearTimeout(state.timer);
      state.timer = null;
    };

    const canAutoplayMobile = (state) => (
      staticMobileQuery.matches &&
      !isReducedMotion() &&
      !document.hidden &&
      state.visible &&
      state.hydrated &&
      state.gallery.length > 1 &&
      !isRectOutsideViewport(
        state.media.getBoundingClientRect(),
        window.innerHeight
      )
    );

    const renderMobileAsset = async (state, slotIndex, assetIndex) => {
      const slot = state.slots[slotIndex];
      const asset = state.gallery[assetIndex];
      const token = state.transitionToken + 1;

      if (!slot || !asset) {
        return false;
      }

      state.transitionToken = token;

      const loadedAsset = await loadGalleryAsset(asset);

      if (!loadedAsset || token !== state.transitionToken) {
        return false;
      }

      const picture = document.createElement('picture');
      const image = document.createElement('img');

      image.src = loadedAsset.resolvedSrc;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 515;
      image.height = 348;
      picture.append(image);
      slot.replaceChildren(picture);

      return true;
    };

    const scheduleMobileAutoplay = (
      state,
      delay = expertiseMobileAutoplayDelay
    ) => {
      stopMobileAutoplay(state);

      if (!canAutoplayMobile(state)) {
        return;
      }

      loadGalleryAsset(
        state.gallery[getWrappedGalleryIndex(state.currentIndex + 1, state.gallery.length)]
      );

      state.timer = window.setTimeout(async () => {
        state.timer = null;

        if (!canAutoplayMobile(state)) {
          return;
        }

        const nextIndex = getWrappedGalleryIndex(
          state.currentIndex + 1,
          state.gallery.length
        );
        const nextSlotIndex = state.activeSlotIndex === 0 ? 1 : 0;
        const ready = await renderMobileAsset(state, nextSlotIndex, nextIndex);

        if (!ready || !canAutoplayMobile(state)) {
          scheduleMobileAutoplay(state);
          return;
        }

        const previousSlot = state.slots[state.activeSlotIndex];
        const nextSlot = state.slots[nextSlotIndex];

        nextSlot.classList.add('is-active');
        previousSlot.classList.remove('is-active');
        state.activeSlotIndex = nextSlotIndex;
        state.currentIndex = nextIndex;
        window.setTimeout(() => {
          if (!previousSlot.classList.contains('is-active')) {
            previousSlot.replaceChildren();
          }
        }, expertiseMobileFadeDuration);
        loadGalleryAsset(
          state.gallery[getWrappedGalleryIndex(nextIndex + 1, state.gallery.length)]
        );
        scheduleMobileAutoplay(state);
      }, delay);
    };

    const scheduleMobileAutoplayStart = (state) => {
      scheduleMobileAutoplay(
        state,
        getStaggeredAutoplayDelay(
          state.index,
          expertiseMobileAutoplayDelay,
          expertiseMobileAutoplayStagger
        )
      );
    };

    const syncMobileAutoplay = () => {
      mobileStates.forEach((state) => {
        if (canAutoplayMobile(state)) {
          scheduleMobileAutoplayStart(state);
        } else {
          stopMobileAutoplay(state);
        }
      });
    };

    const hydrateMobileMedia = (media) => {
      if (media.dataset.expertiseMobileLoaded === 'true') {
        return;
      }

      media.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });

      media.querySelectorAll('img[data-src]').forEach((image) => {
        if (!image.dataset.src) {
          return;
        }

        image.src = image.dataset.src;
        delete image.dataset.src;
      });

      const poster = media.querySelector('.expertise-sec__media-poster');
      const image = media.querySelector('picture img');

      if (poster && image) {
        watchImageReady(image, () => {
          retireExpertisePoster(media, poster);
        });
      }

      const state = mobileStates.find((item) => item.media === media);

      if (state?.track && !state.hydrated) {
        const firstSlot = document.createElement('div');
        const secondSlot = document.createElement('div');
        const initialNodes = Array.from(state.track.childNodes);

        firstSlot.className = 'expertise-sec__mobile-slide is-active';
        secondSlot.className = 'expertise-sec__mobile-slide';
        firstSlot.append(...initialNodes);
        state.track.append(firstSlot, secondSlot);
        state.slots = [firstSlot, secondSlot];
        state.hydrated = true;
        loadGalleryAsset(state.gallery[0]);
        scheduleMobileAutoplayStart(state);
      }

      media.dataset.expertiseMobileLoaded = 'true';
    };

    const initMobileMediaLoading = () => {
      if (!mobileMedia.length || !staticMobileQuery.matches) {
        return;
      }

      if (typeof window.IntersectionObserver !== 'function') {
        mobileStates.forEach((state) => {
          state.visible = true;
          hydrateMobileMedia(state.media);
        });
        return;
      }

      if (mobileMediaObserver) {
        return;
      }

      mobileMediaObserver = new window.IntersectionObserver((entries) => {
        if (!staticMobileQuery.matches) {
          return;
        }

        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          hydrateMobileMedia(entry.target);
          mobileMediaObserver.unobserve(entry.target);
        });
      }, {
        rootMargin: '300px 0px',
        threshold: 0.01,
      });

      mobileMedia.forEach((media) => mobileMediaObserver.observe(media));

      if (!mobileVisibilityObserver) {
        mobileVisibilityObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = mobileStates.find((item) => item.media === entry.target);

            if (!state) {
              return;
            }

            state.visible = entry.isIntersecting && entry.intersectionRatio > 0.1;

            if (state.visible) {
              scheduleMobileAutoplayStart(state);
            } else {
              stopMobileAutoplay(state);
            }
          });
        }, { threshold: [0, 0.1, 0.5] });

        mobileMedia.forEach((media) => mobileVisibilityObserver.observe(media));
      }
    };

    const measureLayer = () => {
      const rect = mediaLayer.getBoundingClientRect();

      layerSize = {
        width: rect.width || mediaLayer.offsetWidth,
        height: rect.height || mediaLayer.offsetHeight,
      };
      trailSize = getElementLayoutSize(trailSlots[0]?.el, layerSize);
    };

    const updateContrastMask = () => {
      if (!contrastLayer || !displayedRow || interactionMode === 'trail') {
        return;
      }

      const corners = getRotatedRectCorners({
        x: rendered.x,
        y: rendered.y,
        width: layerSize.width,
        height: layerSize.height,
        rotation: rendered.rotation,
      });
      const clipPath = getLocalClipPolygon(corners, { left: 0, top: 0 });

      if (clipPath === lastAppliedClipPath) {
        return;
      }

      lastAppliedClipPath = clipPath;
      contrastLayer.style.clipPath = clipPath;
      contrastLayer.style.webkitClipPath = clipPath;
    };

    const measureContrastTitles = () => {
      if (!contrastTitles.length) {
        return;
      }

      const rects = titles.map((title) => title.getBoundingClientRect());

      contrastTitles.forEach((contrastTitle, index) => {
        const rect = rects[index];

        contrastTitle.style.left = `${rect.left}px`;
        contrastTitle.style.top = `${rect.top}px`;
        contrastTitle.style.width = `${rect.width}px`;
        contrastTitle.style.height = `${rect.height}px`;
      });
    };

    const scheduleContrastPositionUpdate = () => {
      if (contrastPositionFrame) {
        return;
      }

      contrastPositionFrame = window.requestAnimationFrame(() => {
        contrastPositionFrame = null;
        measureContrastTitles();
      });
    };

    const commitContrastTitle = (row) => {
      contrastTitles.forEach((contrastTitle) => {
        contrastTitle.classList.remove('is-displayed');

        if (!contrastTitle.classList.contains('is-flip-active')) {
          contrastTitle.classList.add('is-flip-idle');
        }
      });
      displayedContrastTitle = contrastTitles[getRowIndex(row)] || null;
      displayedContrastTitle?.classList.remove('is-flip-idle');
    };

    const applyLayerTransform = (force = false) => {
      const transformChanged = force ||
        rendered.x !== lastAppliedTransform.x ||
        rendered.y !== lastAppliedTransform.y ||
        rendered.rotation !== lastAppliedTransform.rotation;

      if (!transformChanged) {
        updateContrastMask();
        return;
      }

      lastAppliedTransform = { ...rendered };

      if (setLayerX && setLayerY && setLayerRotation) {
        setLayerX(rendered.x);
        setLayerY(rendered.y);
        setLayerRotation(rendered.rotation);
      } else {
        mediaLayer.style.transform = `translate3d(${rendered.x}px, ${rendered.y}px, 0) rotate(${rendered.rotation}deg)`;
      }

      updateContrastMask();
    };

    const cancelSlotPlay = (slot) => {
      if (!slot?.playTimer) {
        return;
      }

      window.clearTimeout(slot.playTimer);
      slot.playTimer = null;
    };

    const pauseSlotVideo = (slot) => {
      cancelSlotPlay(slot);
      slot?.video?.pause();
    };

    const playSlotVideo = (slot) => {
      if (!slot?.video || slot.playTimer || !slot.video.paused) {
        return;
      }

      const slotIndex = slots.indexOf(slot);

      slot.playTimer = window.setTimeout(() => {
        slot.playTimer = null;

        if (
          slotIndex !== activeSlotIndex ||
          !slot.el.classList.contains('is-active') ||
          !layerVisible ||
          !slot.video
        ) {
          return;
        }

        if (slot.video.preload === 'none') {
          slot.video.preload = 'auto';
          slot.video.dataset.expertiseDecoderStarted = 'true';
          slot.video.load();
        }

        slot.video.play().catch(() => {});
      }, isReducedMotion() ? 0 : mediaVideoStartDelay);
    };

    const pauseAllVideos = () => {
      slots.forEach(pauseSlotVideo);
    };

    const renderVideo = (row, signal) => {
      if (!row.dataset.expertiseVideoSrc) {
        return null;
      }

      const videoWrap = document.createElement('div');
      const readiness = [];
      const hasPoster = !!row.dataset.expertisePoster;
      let poster = null;
      let disposePosterRetirement = () => {};

      videoWrap.className = 'expertise-sec__video';

      if (hasPoster) {
        videoWrap.classList.add('expertise-sec__poster-host', 'has-poster');
        poster = document.createElement('img');

        poster.className = 'expertise-sec__video-poster expertise-sec__media-poster';
        poster.src = row.dataset.expertisePoster;
        poster.alt = '';
        poster.loading = 'eager';
        poster.decoding = 'async';
        videoWrap.append(poster);
        readiness.push(waitForImage(poster, mediaReadyTimeout, signal));
      }

      const video = document.createElement('video');

      video.className = hasPoster
        ? 'expertise-sec__video-el expertise-sec__media-content'
        : 'expertise-sec__video-el';
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = hasPoster ? 'none' : 'metadata';

      if (hasPoster) {
        video.poster = row.dataset.expertisePoster;
      }

      video.src = row.dataset.expertiseVideoSrc;
      const handleVideoReady = () => {
        videoWrap.classList.add('is-video-ready');

        if (poster) {
          disposePosterRetirement = retireExpertisePoster(videoWrap, poster);
        }
      };

      video.addEventListener('loadeddata', handleVideoReady, { once: true });
      videoWrap.append(video);

      if (!hasPoster) {
        readiness.push(waitForVideo(video, mediaReadyTimeout, signal));
        video.dataset.expertiseDecoderStarted = 'true';
        video.load();
      }

      return {
        element: videoWrap,
        video,
        ready: waitForFirstReady(readiness),
        dispose: () => {
          const decoderStarted = video.dataset.expertiseDecoderStarted === 'true';

          disposePosterRetirement();
          video.removeEventListener('loadeddata', handleVideoReady);
          video.pause();
          video.removeAttribute('src');
          video.removeAttribute('poster');

          if (decoderStarted) {
            video.load();
          }

          poster?.removeAttribute('src');
        },
      };
    };

    const renderImage = (row, signal, asset = null) => {
      const resolvedSrc = asset?.resolvedSrc || '';
      const imageSrc = resolvedSrc || asset?.src || row.dataset.expertiseImgSrc;
      const imageWebp = resolvedSrc && resolvedSrc !== asset?.webp
        ? ''
        : asset?.webp || row.dataset.expertiseImgWebp;
      const posterSrc = asset?.poster || row.dataset.expertisePosterSrc;
      const hasOverlay = asset
        ? asset.overlay
        : row.dataset.expertiseOverlay === 'true';

      if (!imageSrc) {
        return null;
      }

      const picture = document.createElement('picture');
      const posterHost = posterSrc ? document.createElement('div') : null;
      let poster = null;
      let disposePosterRetirement = () => {};

      if (posterHost) {
        posterHost.className = 'expertise-sec__poster-host has-poster';
        picture.className = 'expertise-sec__media-content';
        poster = document.createElement('img');
        poster.className = 'expertise-sec__media-poster';
        poster.src = posterSrc;
        poster.alt = '';
        poster.loading = 'eager';
        poster.decoding = 'async';
        posterHost.append(poster, picture);
      }

      if (hasOverlay) {
        (posterHost || picture).classList.add('has-overlay');
      }

      if (imageWebp) {
        const source = document.createElement('source');

        source.type = 'image/webp';
        source.srcset = imageWebp;
        picture.append(source);
      }

      const image = document.createElement('img');

      image.src = imageSrc;
      image.alt = '';
      image.loading = 'eager';
      image.decoding = 'async';
      picture.append(image);

      const contentReady = waitForImage(image, mediaReadyTimeout, signal);
      const posterReady = poster
        ? waitForImage(poster, mediaReadyTimeout, signal)
        : contentReady;
      const stopWatchingImage = poster
        ? watchImageReady(image, () => {
          disposePosterRetirement = retireExpertisePoster(posterHost, poster);
        })
        : () => {};

      return {
        element: posterHost || picture,
        video: null,
        ready: waitForFirstReady([posterReady, contentReady]),
        dispose: () => {
          stopWatchingImage();
          disposePosterRetirement();
          image.removeAttribute('src');
          poster?.removeAttribute('src');
          picture.querySelectorAll('source').forEach((source) => {
            source.removeAttribute('srcset');
          });
        },
      };
    };

    const disposeSlotMedia = (slot) => {
      pauseSlotVideo(slot);
      slot.loadController?.abort();
      slot.loadController = null;
      slot.dispose?.();
      slot.dispose = null;
      slot.video = null;
      slot.el.replaceChildren();
    };

    const setSlotMedia = (slot, row, asset = null) => {
      const mediaKey = getMediaKey(row, asset);

      if (slot.key === mediaKey && slot.el.firstElementChild) {
        if (slot.status === 'ready') {
          return Promise.resolve(true);
        }

        if (slot.status === 'loading') {
          return slot.readyPromise;
        }
      }

      disposeSlotMedia(slot);
      slot.loadToken += 1;
      slot.key = mediaKey;
      slot.status = 'loading';
      slot.loadController = new AbortController();

      const media = row.dataset.expertiseMediaType === 'image'
        ? renderImage(row, slot.loadController.signal, asset)
        : renderVideo(row, slot.loadController.signal);

      if (!media) {
        slot.loadController.abort();
        slot.loadController = null;
        slot.key = '';
        slot.status = 'error';
        return Promise.resolve(false);
      }

      const loadToken = slot.loadToken;

      slot.el.append(media.element);
      slot.video = media.video;
      slot.dispose = media.dispose;
      slot.readyPromise = media.ready.then((ready) => {
        if (slot.loadToken !== loadToken) {
          return false;
        }

        slot.status = ready ? 'ready' : 'error';

        if (!ready) {
          slot.key = '';
        }

        return ready;
      });

      return slot.readyPromise;
    };

    const getSlotOpacity = (slot) => {
      const opacity = gsapInstance
        ? Number(gsapInstance.getProperty(slot.el, 'opacity'))
        : Number(getComputedStyle(slot.el).opacity);

      return Number.isFinite(opacity) ? opacity : 0;
    };

    const setSlotImmediate = (slotIndex, visible) => {
      const slot = slots[slotIndex];

      if (!slot) {
        return;
      }

      slot.el.classList.toggle('is-active', visible);

      if (gsapInstance) {
        gsapInstance.set(slot.el, {
          autoAlpha: visible ? 1 : 0,
          scale: visible ? 1 : 0.975,
        });
      } else {
        slot.el.style.removeProperty('opacity');
        slot.el.style.removeProperty('visibility');
        slot.el.style.removeProperty('transform');
      }

      if (!visible) {
        pauseSlotVideo(slot);
      }
    };

    const setContrastActive = (
      visible,
      duration = visible ? mediaSwitchInDuration : mediaSwitchOutDuration
    ) => {
      displayedContrastTitle?.style.setProperty('--expertise-contrast-duration', `${duration}s`);
      displayedContrastTitle?.classList.toggle('is-displayed', visible);
    };

    const idleHiddenContrastTitles = () => {
      contrastTitles.forEach((contrastTitle) => {
        if (!contrastTitle.classList.contains('is-displayed')) {
          contrastTitle.classList.add('is-flip-idle');
        }
      });
    };

    const applyTrailContrastMask = () => {
      if (!contrastLayer) {
        return;
      }

      contrastLayer.style.clipPath = `url(#${trailClipId})`;
      contrastLayer.style.webkitClipPath = `url(#${trailClipId})`;
      lastAppliedClipPath = '';
    };

    const clearTrailContrastMask = () => {
      trailSlots.forEach((slot) => slot.polygon.removeAttribute('points'));
      contrastLayer?.style.removeProperty('clip-path');
      contrastLayer?.style.removeProperty('-webkit-clip-path');
      lastAppliedClipPath = '';
    };

    const updateTrailMask = () => {
      trailMaskFrame = null;

      trailSlots.forEach((slot) => {
        if (!slot.active) {
          slot.polygon.removeAttribute('points');
          return;
        }

        const scale = Number(gsapInstance?.getProperty(slot.el, 'scale'));
        const corners = getRotatedRectCorners({
          x: slot.x,
          y: slot.y,
          width: trailSize.width,
          height: trailSize.height,
          rotation: slot.rotation,
          scale: Number.isFinite(scale) ? scale : 1,
        });

        slot.polygon.setAttribute('points', getSvgPolygonPoints(corners));
      });
    };

    const scheduleTrailMaskUpdate = () => {
      if (trailMaskFrame) {
        return;
      }

      trailMaskFrame = window.requestAnimationFrame(updateTrailMask);
    };

    const updateTrailPositions = () => {
      trailPositionFrame = null;
      const rowRects = new Map();

      trailSlots.forEach((slot) => {
        if (!slot.active || !slot.row || !slot.point) {
          return;
        }

        if (!rowRects.has(slot.row)) {
          const rowTitle = titles[getRowIndex(slot.row)];

          rowRects.set(
            slot.row,
            (rowTitle || slot.row).getBoundingClientRect()
          );
        }

        const targetPosition = getRowBoundedViewportTarget({
          layerSize: trailSize,
          point: slot.point,
          rowRect: rowRects.get(slot.row),
          viewport: { width: window.innerWidth, height: window.innerHeight },
          safePadding: viewportSafePadding,
          verticalRange: expertiseTrailVerticalRange,
        });

        slot.x = targetPosition.x;
        slot.y = targetPosition.y;
        gsapInstance?.set(slot.el, { x: slot.x, y: slot.y });
      });

      scheduleTrailMaskUpdate();
    };

    const scheduleTrailPositionUpdate = () => {
      if (trailPositionFrame || !trailSlots.some((slot) => slot.active)) {
        return;
      }

      trailPositionFrame = window.requestAnimationFrame(updateTrailPositions);
    };

    const finishTrailVisibility = () => {
      if (trailActiveRow || trailSlots.some((slot) => slot.active)) {
        return;
      }

      trailLayer.classList.remove('is-visible');
      contrastLayer?.classList.remove('is-visible', 'is-motion-active');
      setContrastActive(false, mediaHideDuration);
      idleHiddenContrastTitles();
      clearTrailContrastMask();
      displayedRow = null;
      displayedTitle = null;
    };

    const releaseTrailSlot = (slot) => {
      slot.timeline = null;
      slot.active = false;
      slot.state = 'idle';
      slot.row = null;
      slot.point = null;
      slot.el.classList.remove('is-active', 'is-current', 'is-retiring');
      slot.image.removeAttribute('src');
      slot.polygon.removeAttribute('points');
      gsapInstance?.set(slot.el, { autoAlpha: 0, scale: 0 });
      gsapInstance?.set(slot.overlay, { opacity: 0 });
      scheduleTrailMaskUpdate();
      finishTrailVisibility();
    };

    const retireTrailSlot = (slot, immediate = false) => {
      if (!slot?.active) {
        return;
      }

      slot.timeline?.kill();
      slot.timeline = null;
      slot.state = 'retiring';
      slot.el.classList.remove('is-current');
      slot.el.classList.add('is-retiring');

      if (immediate) {
        releaseTrailSlot(slot);
        return;
      }

      slot.timeline = gsapInstance.timeline({
        onUpdate: scheduleTrailMaskUpdate,
        onComplete: () => releaseTrailSlot(slot),
      });
      slot.timeline
        .to(slot.overlay, {
          opacity: 0.3,
          duration: expertiseTrailOverlayDuration,
          ease: 'power1.out',
        })
        .to(slot.el, {
          scale: 0,
          duration: expertiseTrailOutDuration,
          ease: 'power2.in',
        }, '<');
    };

    const retireCurrentTrailSlot = (immediate = false) => {
      const currentIndex = getCurrentTrailSlotIndex(trailSlots);

      if (currentIndex !== -1) {
        retireTrailSlot(trailSlots[currentIndex], immediate);
      }
    };

    const stopTrailSlots = () => {
      trailSlots.forEach((slot) => {
        slot.timeline?.kill();
        releaseTrailSlot(slot);
      });
      window.cancelAnimationFrame(trailMaskFrame);
      trailMaskFrame = null;
      window.cancelAnimationFrame(trailPositionFrame);
      trailPositionFrame = null;
      updateTrailMask();
    };

    const spawnTrailMedia = async (row, point, force = false) => {
      if (!row || row !== trailActiveRow || !canUseTrail()) {
        return;
      }

      const now = performance.now();

      if (!force && !shouldSpawnExpertiseTrail({
        point,
        lastPoint: trailLastPoint,
        now,
        lastTime: trailLastTime,
        distanceThreshold: expertiseTrailDistance,
        delayThreshold: expertiseTrailDelay,
      })) {
        return;
      }

      const gallery = rowGalleries.get(row) || [];
      const preferredIndex = getWrappedGalleryIndex(trailGalleryIndex, gallery.length);
      const activationToken = trailActivationToken;
      let selected = force ? null : getReadyGalleryAsset(gallery, preferredIndex);

      trailLastPoint = { ...point };
      trailLastTime = now;

      if (!selected && force && preferredIndex >= 0) {
        const loadedAsset = await loadGalleryAsset(gallery[preferredIndex]);

        if (
          !loadedAsset ||
          activationToken !== trailActivationToken ||
          row !== trailActiveRow
        ) {
          return;
        }

        selected = { asset: loadedAsset, index: preferredIndex };
      }

      if (!selected || row !== trailActiveRow) {
        return;
      }

      const slotIndex = getReusableTrailSlotIndex(trailSlots);
      const slot = trailSlots[slotIndex];

      if (!slot) {
        return;
      }

      if (slot.active) {
        slot.timeline?.kill();
        releaseTrailSlot(slot);
      }

      slot.active = true;
      const previousCurrentIndex = promoteTrailSlot(trailSlots, slotIndex);

      if (previousCurrentIndex !== -1) {
        retireTrailSlot(trailSlots[previousCurrentIndex]);
      }

      slot.startedAt = now;
      slot.row = row;
      slot.point = { ...point };
      slot.image.src = selected.asset.resolvedSrc;
      slot.el.classList.remove('is-retiring');
      slot.el.classList.add('is-active', 'is-current');

      const rowTitle = titles[getRowIndex(row)];
      const targetPosition = getRowBoundedViewportTarget({
        layerSize: trailSize,
        point,
        rowRect: (rowTitle || row).getBoundingClientRect(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        safePadding: viewportSafePadding,
        verticalRange: expertiseTrailVerticalRange,
      });
      const sideRotation = getMediaSide(row) === 'left'
        ? -mediaBaseRotation
        : mediaBaseRotation;

      slot.x = targetPosition.x;
      slot.y = targetPosition.y;
      slot.rotation = sideRotation + gsapInstance.utils.random(-2, 2, 0.1);
      trailStackIndex += 1;
      trailGalleryIndex = getWrappedGalleryIndex(selected.index + 1, gallery.length);

      gsapInstance.set(slot.el, {
        x: slot.x,
        y: slot.y,
        rotation: slot.rotation,
        scale: 0,
        autoAlpha: 1,
        zIndex: trailStackIndex,
      });
      gsapInstance.set(slot.overlay, { opacity: 0 });
      scheduleTrailMaskUpdate();

      slot.timeline = gsapInstance.timeline({
        onUpdate: scheduleTrailMaskUpdate,
        onComplete: () => {
          if (slot.state === 'current') {
            slot.timeline = null;
          }
        },
      });
      slot.timeline
        .to(slot.el, {
          scale: 1,
          duration: expertiseTrailInDuration,
          ease: 'back.out(1.7)',
        });
    };

    const activateTrail = (row, title, point) => {
      window.clearTimeout(hideTimer);
      hideTimer = null;
      trailActivationToken += 1;
      trailActiveRow = row;
      trailLastPoint = null;
      trailLastTime = 0;
      trailGalleryIndex = 0;
      displayedRow = row;
      displayedTitle = title;
      commitContrastTitle(row);
      setContrastActive(true, expertiseTrailInDuration);
      applyTrailContrastMask();
      trailLayer.classList.add('is-visible');
      contrastLayer?.classList.add('is-visible', 'is-motion-active');
      mediaLayer.classList.remove('is-visible', 'is-motion-active', 'is-transitioning');
      pauseAllVideos();
      layerVisible = false;
      measureLayer();
      preloadRowGallery(row, true);
      spawnTrailMedia(row, point, true);
    };

    const deactivateTrail = (immediate = false) => {
      trailActivationToken += 1;
      trailActiveRow = null;
      trailLastPoint = null;
      trailLastTime = 0;

      if (immediate) {
        stopTrailSlots();
      } else {
        retireCurrentTrailSlot(false);
        finishTrailVisibility();
      }
    };

    const settleSlots = (slotIndex) => {
      slots.forEach((slot, index) => setSlotImmediate(index, index === slotIndex));
      setContrastActive(true);
      activeSlotIndex = slotIndex;
      playSlotVideo(slots[slotIndex]);
      mediaLayer.classList.remove('is-transitioning');
    };

    const killCrossfade = () => {
      window.clearTimeout(pauseTimer);
      pauseTimer = null;
      slots.forEach(cancelSlotPlay);

      if (transitionTimeline) {
        transitionTimeline.kill();
        transitionTimeline = null;
      }

      if (gsapInstance) {
        slots.forEach((slot) => gsapInstance.killTweensOf(slot.el));
      }

      mediaLayer.classList.remove('is-transitioning');
    };

    const killSlotAnimations = () => {
      killCrossfade();
    };

    const updateTarget = (usePointer = true, deltaMs = 1000 / 60) => {
      if (!displayedRow || !displayedTitle) {
        return;
      }

      const side = getMediaSide(displayedRow);
      const point = usePointer ? pointer : getTitlePoint(displayedTitle);
      const velocityX = usePointer ? pointerVelocityX : 0;
      const viewportTarget = getViewportTarget({
        layerSize,
        point,
        side,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        safePadding: viewportSafePadding,
        horizontalBias: {
          left: mediaOddCursorBias,
          right: mediaEvenCursorBias,
        },
        verticalBias: mediaVerticalCursorBias,
      });

      target = {
        x: viewportTarget.x,
        y: viewportTarget.y,
        rotation: isReducedMotion()
          ? 0
          : getMediaRotation(
            side,
            velocityX,
            mediaFlattenVelocity,
            mediaBaseRotation,
            mediaFlattenAmount
          ),
      };
    };

    const seedMotion = (usePointer = true) => {
      updateTarget(usePointer);
      rendered = { ...target };
      applyLayerTransform(true);
    };

    const isMotionSettled = () => (
      Math.abs(target.x - rendered.x) <= motionPositionEpsilon &&
      Math.abs(target.y - rendered.y) <= motionPositionEpsilon &&
      Math.abs(target.rotation - rendered.rotation) <= motionRotationEpsilon
    );

    const renderMotion = (_time, deltaTime = 1000 / 60) => {
      if (!tickerActive || !displayedRow || isTouchMode() || isReducedMotion()) {
        return;
      }

      const safeDelta = clamp(deltaTime, 1, 64);

      updateTarget(true, safeDelta);

      const positionEase = getFrameEase(mediaFollowEase, safeDelta);
      const rotationEase = getFrameEase(
        getMediaRotationEase(
          rendered.rotation,
          target.rotation,
          mediaFlattenEase,
          mediaRotationEase
        ),
        safeDelta
      );

      rendered = {
        x: rendered.x + (target.x - rendered.x) * positionEase,
        y: rendered.y + (target.y - rendered.y) * positionEase,
        rotation: rendered.rotation + (target.rotation - rendered.rotation) * rotationEase,
      };

      if (isMotionSettled()) {
        rendered = { ...target };
      }

      applyLayerTransform();

      pointerVelocityX += (0 - pointerVelocityX) * getFrameEase(
        mediaVelocityDecayEase,
        safeDelta
      );

      if (isMotionSettled()) {
        stopTicker();
      }
    };

    const fallbackTick = (time) => {
      if (!tickerActive) {
        return;
      }

      const deltaTime = lastFrameTime ? time - lastFrameTime : 1000 / 60;

      lastFrameTime = time;
      renderMotion(time / 1000, deltaTime);

      if (tickerActive) {
        rafFrame = window.requestAnimationFrame(fallbackTick);
      }
    };

    const startTicker = () => {
      if (
        tickerActive ||
        displayedRow !== activeRow ||
        interactionMode !== 'pointer' ||
        isTouchMode() ||
        isReducedMotion()
      ) {
        return;
      }

      tickerActive = true;
      lastFrameTime = 0;

      if (gsapInstance) {
        gsapInstance.ticker.add(renderMotion);
        return;
      }

      rafFrame = window.requestAnimationFrame(fallbackTick);
    };

    const stopTicker = () => {
      if (!tickerActive) {
        return;
      }

      tickerActive = false;

      if (gsapInstance) {
        gsapInstance.ticker.remove(renderMotion);
      } else if (rafFrame) {
        window.cancelAnimationFrame(rafFrame);
      }

      rafFrame = null;
      lastFrameTime = 0;
    };

    const syncTicker = () => {
      if (
        displayedRow === activeRow &&
        interactionMode === 'pointer' &&
        !isTouchMode() &&
        !isReducedMotion() &&
        layerVisible
      ) {
        startTicker();
        return;
      }

      stopTicker();
    };

    const showLayer = (withReveal = false) => {
      const side = displayedRow ? getMediaSide(displayedRow) : 'right';

      window.clearTimeout(hideTimer);
      hideTimer = null;
      layerVisible = true;
      mediaLayer.classList.add('is-visible', 'is-motion-active');
      contrastLayer?.classList.add('is-visible', 'is-motion-active');

      if (!gsapInstance || isReducedMotion()) {
        if (gsapInstance) {
          gsapInstance.set(mediaInner, { xPercent: 0, scale: 1 });
        }

        syncTicker();
        return;
      }

      gsapInstance.killTweensOf(mediaInner);

      if (withReveal) {
        gsapInstance.fromTo(
          mediaInner,
          {
            xPercent: side === 'left' ? -8 : 8,
            scale: 0.96,
          },
          {
            xPercent: 0,
            scale: 1,
            duration: 0.34,
            ease: 'power3.out',
          }
        );
      } else {
        gsapInstance.to(mediaInner, {
          xPercent: 0,
          scale: 1,
          duration: 0.2,
          ease: 'power2.out',
        });
      }

      syncTicker();
    };

    const hideLayer = () => {
      if (!layerVisible) {
        contrastLayer?.classList.remove('is-visible', 'is-motion-active');
        setContrastActive(false, mediaHideDuration);
        idleHiddenContrastTitles();
        pauseAllVideos();
        return;
      }

      layerVisible = false;
      stopTicker();
      mediaLayer.classList.remove('is-visible', 'is-transitioning');
      contrastLayer?.classList.remove('is-visible');
      setContrastActive(false, mediaHideDuration);

      if (isReducedMotion()) {
        gsapInstance?.set(mediaInner, { xPercent: 0, scale: 1 });
        mediaLayer.classList.remove('is-motion-active');
        contrastLayer?.classList.remove('is-motion-active');
        idleHiddenContrastTitles();
        pauseAllVideos();
        return;
      }

      if (gsapInstance) {
        gsapInstance.killTweensOf(mediaInner);
        gsapInstance.to(mediaInner, {
          xPercent: displayedRow && getMediaSide(displayedRow) === 'left' ? -5 : 5,
          scale: 0.97,
          duration: mediaHideDuration,
          ease: 'power2.out',
        });
      }

      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        mediaLayer.classList.remove('is-motion-active');
        contrastLayer?.classList.remove('is-motion-active');
        idleHiddenContrastTitles();
        pauseAllVideos();
      }, mediaHideDuration * 1000);
    };

    const commitDisplayedMediaContext = (row, title, mode) => {
      displayedRow = row;
      displayedTitle = title;

      const side = getMediaSide(row);
      const usePointer = mode === 'pointer' && !isTouchMode() && !isReducedMotion();

      mediaLayer.classList.toggle('is-left-side', side === 'left');
      mediaLayer.classList.toggle('is-right-side', side === 'right');

      if (!usePointer) {
        pointer = getTitlePoint(title);
      }

      commitContrastTitle(row);
      pointerVelocityX = 0;
      lastPointerMoveTime = 0;
      seedMotion(usePointer);
    };

    const crossfadeTo = (slotIndex, mediaContext) => {
      const { row, title, mode } = mediaContext;
      const wasHidden = !layerVisible;
      const targetSlot = slots[slotIndex];
      const sameDisplayedContext = displayedRow === row && displayedTitle === title;
      const reusingVisibleTarget = (
        !wasHidden &&
        !sameDisplayedContext &&
        activeSlotIndex === slotIndex &&
        getSlotOpacity(targetSlot) > 0.01
      );

      killCrossfade();

      if (
        !wasHidden &&
        sameDisplayedContext &&
        activeSlotIndex === slotIndex &&
        getSlotOpacity(targetSlot) >= 0.99
      ) {
        settleSlots(slotIndex);
        return;
      }

      if (wasHidden) {
        slots.forEach((_slot, index) => setSlotImmediate(index, false));
        setContrastActive(false);
      }

      const outgoingSlots = slots.filter((slot, index) => (
        (index !== slotIndex || reusingVisibleTarget) &&
        slot.el.firstElementChild &&
        getSlotOpacity(slot) > 0.01
      ));
      const outgoingElements = outgoingSlots.map((slot) => slot.el);
      const outgoingOpacity = outgoingSlots.reduce(
        (maximum, slot) => Math.max(maximum, getSlotOpacity(slot)),
        0
      );
      const fadeOutDuration = Math.max(
        0.08,
        mediaSwitchOutDuration * clamp(outgoingOpacity, 0, 1)
      );
      const commitTargetMediaContext = () => {
        const currentMode = row === activeRow ? interactionMode : mode;

        commitDisplayedMediaContext(row, title, currentMode);
      };
      const revealTarget = () => {
        targetSlot.el.classList.add('is-active');
        setContrastActive(true);
        activeSlotIndex = slotIndex;
        playSlotVideo(targetSlot);
        syncTicker();
      };
      const hideOutgoing = () => {
        outgoingSlots.forEach((slot) => {
          slot.el.classList.remove('is-active');
          pauseSlotVideo(slot);
        });
      };

      targetSlot.el.classList.remove('is-active');

      if (isReducedMotion()) {
        commitTargetMediaContext();
        showLayer(wasHidden);
        settleSlots(slotIndex);
        syncTicker();
        return;
      }

      mediaLayer.classList.add('is-transitioning');

      if (outgoingSlots.length) {
        setContrastActive(false, fadeOutDuration);
      }

      if (!gsapInstance) {
        const startIncomingFade = () => {
          hideOutgoing();
          commitTargetMediaContext();
          showLayer(wasHidden);
          revealTarget();
          pauseTimer = window.setTimeout(() => {
            mediaLayer.classList.remove('is-transitioning');
          }, mediaSwitchInDuration * 1000);
        };

        if (!outgoingSlots.length) {
          startIncomingFade();
          return;
        }

        outgoingSlots.forEach((slot) => slot.el.classList.remove('is-active'));
        pauseTimer = window.setTimeout(
          startIncomingFade,
          fadeOutDuration * 1000
        );
        return;
      }

      if (sameDisplayedContext && activeSlotIndex === slotIndex) {
        targetSlot.el.classList.add('is-active');
        setContrastActive(true);
        showLayer(wasHidden);
        transitionTimeline = gsapInstance.timeline({
          onComplete: () => {
            transitionTimeline = null;
            settleSlots(slotIndex);
            syncTicker();
          },
        });
        transitionTimeline.to(targetSlot.el, {
          autoAlpha: 1,
          scale: 1,
          duration: mediaSwitchInDuration * (1 - clamp(getSlotOpacity(targetSlot), 0, 1)),
          ease: 'sine.out',
        });
        return;
      }

      if (!reusingVisibleTarget) {
        gsapInstance.set(targetSlot.el, {
          autoAlpha: 0,
          scale: 0.975,
        });
      }
      transitionTimeline = gsapInstance.timeline({
        onComplete: () => {
          transitionTimeline = null;
          settleSlots(slotIndex);
          syncTicker();
        },
      });

      if (!outgoingElements.length) {
        commitTargetMediaContext();
        showLayer(wasHidden);
        revealTarget();
        transitionTimeline.to(targetSlot.el, {
          autoAlpha: 1,
          scale: 1,
          duration: mediaSwitchInDuration,
          ease: 'sine.out',
        });
        return;
      }

      transitionTimeline
        .to(outgoingElements, {
          autoAlpha: 0,
          scale: 0.975,
          duration: fadeOutDuration,
          ease: 'sine.inOut',
        })
        .add(hideOutgoing)
        .add(commitTargetMediaContext)
        .add(revealTarget)
        .to(targetSlot.el, {
          autoAlpha: 1,
          scale: 1,
          duration: mediaSwitchInDuration,
          ease: 'sine.out',
        });
    };

    const handleMediaFailure = (row, token) => {
      if (token !== mediaRequestToken || row !== activeRow) {
        return;
      }

      row.classList.add('is-media-unavailable');
      killSlotAnimations();
      hideLayer();
      displayedRow = null;
      displayedTitle = null;
    };

    const transitionMedia = async (row, title, mode, asset = null) => {
      const token = mediaRequestToken + 1;
      const mediaKey = getMediaKey(row, asset);

      mediaRequestToken = token;
      row.classList.remove('is-media-unavailable');
      killSlotAnimations();

      if (displayedRow !== row) {
        stopTicker();
      }

      if (!hasRowMedia(row)) {
        handleMediaFailure(row, token);
        return;
      }

      const opacities = slots.map((slot) => (
        slot.el.firstElementChild ? getSlotOpacity(slot) : -1
      ));
      const dominantIndex = getDominantSlotIndex(opacities, activeSlotIndex);

      if (dominantIndex >= 0) {
        activeSlotIndex = dominantIndex;
      }

      let targetSlotIndex = slots.findIndex((slot) => (
        slot.key === mediaKey &&
        slot.el.firstElementChild &&
        slot.status !== 'error'
      ));

      if (targetSlotIndex < 0) {
        targetSlotIndex = slots.length;
        slots.push(createMediaSlot());
      }

      const ready = await setSlotMedia(slots[targetSlotIndex], row, asset);

      if (token !== mediaRequestToken || row !== activeRow) {
        return;
      }

      if (!ready) {
        handleMediaFailure(row, token);
        return;
      }

      crossfadeTo(targetSlotIndex, { row, title, mode });
    };

    const setActiveRow = (row, event = null, mode = 'pointer', asset = null) => {
      const title = row.querySelector('[data-expertise-title]');

      if (!title) {
        return;
      }

      activeRow = row;
      activeTitle = title;
      interactionMode = mode;
      pointer = mode === 'pointer'
        ? getInteractionPoint(event, title)
        : getTitlePoint(title);
      pointerVelocityX = 0;
      lastPointerMoveTime = 0;
      rows.forEach((currentRow, index) => {
        const isActive = currentRow === row;
        const contrastTitle = contrastTitles[index];

        currentRow.classList.toggle('is-active', isActive);
        titles[index].setAttribute('aria-pressed', String(isActive));

        if (isActive) {
          contrastTitle?.classList.remove('is-flip-idle');
        } else if (contrastTitle !== displayedContrastTitle) {
          contrastTitle?.classList.add('is-flip-idle');
        }

        contrastTitles[index]?.classList.toggle('is-flip-active', isActive);
      });

      if (trailActiveRow || trailSlots.some((slot) => slot.active)) {
        deactivateTrail(true);
      }

      transitionMedia(row, title, mode, asset);
    };

    const clearActiveRow = ({ immediateTrail = false } = {}) => {
      const wasTrail = (
        interactionMode === 'trail' ||
        !!trailActiveRow ||
        trailSlots.some((slot) => slot.active)
      );

      window.clearTimeout(hoverLeaveTimer);
      hoverLeaveTimer = null;
      mediaRequestToken += 1;
      stopTicker();
      rows.forEach((row) => row.classList.remove('is-active', 'is-media-unavailable'));
      titles.forEach((title) => title.setAttribute('aria-pressed', 'false'));
      contrastTitles.forEach((contrastTitle) => {
        contrastTitle.classList.remove('is-flip-active');
      });

      if (wasTrail) {
        deactivateTrail(immediateTrail);
      } else {
        killSlotAnimations();
        hideLayer();
      }

      activeRow = null;
      activeTitle = null;
      displayedRow = null;
      displayedTitle = null;
      interactionMode = null;
      mediaLayer.classList.remove('is-left-side', 'is-right-side');
    };

    const repositionActiveLayer = () => {
      if (!displayedRow || !displayedTitle || displayedRow !== activeRow) {
        return;
      }

      const usePointer = interactionMode === 'pointer' && !isTouchMode() && !isReducedMotion();

      if (!usePointer) {
        pointer = getTitlePoint(displayedTitle);
        pointerVelocityX = 0;
        lastPointerMoveTime = 0;
      }

      seedMotion(usePointer);
      syncTicker();
    };

    titles.forEach((title) => {
      const row = title.closest('[data-expertise-row]');

      if (!row) {
        return;
      }

      title.addEventListener('pointerdown', (event) => {
        if (shouldPreventExpertisePointerFocus(event.pointerType)) {
          event.preventDefault();
        }

        pointerFocusTitle = title;
        window.requestAnimationFrame(() => {
          if (pointerFocusTitle === title) {
            pointerFocusTitle = null;
          }
        });
      });

      title.addEventListener('mouseenter', async (event) => {
        if (isTouchMode()) {
          return;
        }

        window.clearTimeout(hoverLeaveTimer);
        hoverLeaveTimer = null;
        const hoverToken = (rowHoverTokens.get(row) || 0) + 1;

        rowHoverTokens.set(row, hoverToken);
        preloadRowGallery(row, true);
        const asset = await getNextRowGalleryAsset(row);

        if (
          rowHoverTokens.get(row) !== hoverToken ||
          !title.matches(':hover') ||
          isTouchMode()
        ) {
          return;
        }

        setActiveRow(row, event, 'pointer', asset);
      });

      title.addEventListener('pointermove', (event) => {
        if (activeRow !== row || interactionMode !== 'trail' || !canUseTrail()) {
          return;
        }

        pointer = toClientPoint(event);
        spawnTrailMedia(row, pointer);
      }, { passive: true });

      title.addEventListener('mouseleave', () => {
        if (isTouchMode()) {
          return;
        }

        rowHoverTokens.set(row, (rowHoverTokens.get(row) || 0) + 1);
        window.clearTimeout(hoverLeaveTimer);
        hoverLeaveTimer = window.setTimeout(() => {
          hoverLeaveTimer = null;

          if (
            activeRow !== row ||
            (interactionMode !== 'pointer' && interactionMode !== 'trail')
          ) {
            return;
          }

          if (title === document.activeElement) {
            if (interactionMode === 'trail') {
              deactivateTrail(true);
              interactionMode = 'keyboard';
              transitionMedia(row, title, 'keyboard');
            } else {
              interactionMode = 'keyboard';
              repositionActiveLayer();
            }
            return;
          }

          clearActiveRow();
        }, hoverLeaveDelay);
      });

      title.addEventListener('focus', () => {
        if (pointerFocusTitle === title) {
          return;
        }

        setActiveRow(row, null, 'keyboard');
      });

      title.addEventListener('blur', () => {
        window.requestAnimationFrame(() => {
          if (titles.includes(document.activeElement)) {
            return;
          }

          if (interactionMode === 'keyboard' || isTouchMode()) {
            clearActiveRow();
          }
        });
      });

      title.addEventListener('click', (event) => {
        if (!isTouchMode()) {
          return;
        }

        event.preventDefault();

        if (activeRow === row && interactionMode === 'touch' && layerVisible) {
          clearActiveRow();
          return;
        }

        setActiveRow(row, event, 'touch');
      });
    });

    section.addEventListener('pointermove', (event) => {
      if (
        !activeRow ||
        interactionMode === 'trail' ||
        interactionMode !== 'pointer' ||
        isTouchMode()
      ) {
        return;
      }

      const nextPointer = toClientPoint(event);

      if (!nextPointer) {
        return;
      }

      const eventTime = Number.isFinite(event.timeStamp)
        ? event.timeStamp
        : performance.now();
      const deltaMs = lastPointerMoveTime
        ? clamp(eventTime - lastPointerMoveTime, 1, 64)
        : 1000 / 60;

      pointerVelocityX = (nextPointer.x - pointer.x) / (deltaMs / 1000);
      pointer = nextPointer;
      lastPointerMoveTime = eventTime;
      startTicker();
    }, { passive: true });

    section.addEventListener('pointerleave', () => {
      if (isTouchMode()) {
        return;
      }

      if (titles.includes(document.activeElement)) {
        if (interactionMode === 'trail') {
          const focusedTitle = document.activeElement;
          const focusedRow = focusedTitle.closest('[data-expertise-row]');

          deactivateTrail(true);
          interactionMode = 'keyboard';

          if (focusedRow) {
            activeRow = focusedRow;
            activeTitle = focusedTitle;
            transitionMedia(focusedRow, focusedTitle, 'keyboard');
          }
        } else {
          interactionMode = 'keyboard';
          repositionActiveLayer();
        }
        return;
      }

      clearActiveRow();
    });

    document.addEventListener('click', (event) => {
      if (!isTouchMode() || !activeRow || section.contains(event.target)) {
        return;
      }

      clearActiveRow();
    });

    const handleSectionVisibility = (entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        return;
      }

      if (activeRow) {
        clearActiveRow({ immediateTrail: true });
      } else if (trailSlots.some((slot) => slot.active)) {
        deactivateTrail(true);
      }
    };

    const handleSectionViewportFallback = () => {
      if (
        (!activeRow && !trailSlots.some((slot) => slot.active)) ||
        sectionVisibilityFrame
      ) {
        return;
      }

      sectionVisibilityFrame = window.requestAnimationFrame(() => {
        sectionVisibilityFrame = null;

        if (
          isRectOutsideViewport(section.getBoundingClientRect(), window.innerHeight)
        ) {
          clearActiveRow({ immediateTrail: true });
        }
      });
    };

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        measureLayer();
        measureContrastTitles();

        if (interactionMode === 'trail') {
          scheduleTrailPositionUpdate();
        } else {
          repositionActiveLayer();
        }
      });
    };

    const handleTouchModeChange = () => {
      if (interactionMode === 'trail' || trailSlots.some((slot) => slot.active)) {
        clearActiveRow({ immediateTrail: true });
        return;
      }

      repositionActiveLayer();
    };

    const handleStaticMobileChange = () => {
      if (staticMobileQuery.matches) {
        clearActiveRow({ immediateTrail: true });
      } else {
        mobileStates.forEach(stopMobileAutoplay);
      }

      initMobileMediaLoading();
      syncMobileAutoplay();
    };

    const handleReducedMotionChange = () => {
      section.classList.toggle('is-reduced-motion', isReducedMotion());
      syncMobileAutoplay();

      if (!activeRow) {
        if (isReducedMotion()) {
          deactivateTrail(true);
        }
        return;
      }

      if (interactionMode === 'trail') {
        const row = activeRow;
        const title = activeTitle;

        deactivateTrail(true);
        interactionMode = 'pointer';
        transitionMedia(row, title, 'pointer');
        return;
      }

      mediaRequestToken += 1;
      killSlotAnimations();
      repositionActiveLayer();
      transitionMedia(activeRow, activeTitle, interactionMode);
    };

    const handleDocumentVisibility = () => {
      if (document.hidden && (trailActiveRow || trailSlots.some((slot) => slot.active))) {
        clearActiveRow({ immediateTrail: true });
      }

      syncMobileAutoplay();
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', scheduleContrastPositionUpdate, { passive: true });
    window.addEventListener('scroll', scheduleTrailPositionUpdate, { passive: true });
    addMediaQueryListener(touchMediaQuery, handleTouchModeChange);
    addMediaQueryListener(staticMobileQuery, handleStaticMobileChange);
    addMediaQueryListener(reducedMotionQuery, handleReducedMotionChange);
    document.addEventListener('visibilitychange', handleDocumentVisibility);

    const sectionVisibilityObserver = typeof window.IntersectionObserver === 'function'
      ? new window.IntersectionObserver(handleSectionVisibility, { threshold: 0 })
      : null;

    if (sectionVisibilityObserver) {
      sectionVisibilityObserver.observe(section);
    } else {
      window.addEventListener('scroll', handleSectionViewportFallback, { passive: true });
    }

    if (typeof window.IntersectionObserver === 'function') {
      sectionPreloadObserver = new window.IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        rows.forEach((row) => preloadRowGallery(row));
        sectionPreloadObserver.disconnect();
        sectionPreloadObserver = null;
      }, {
        rootMargin: '300px 0px',
        threshold: 0.01,
      });
      sectionPreloadObserver.observe(section);
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        measureLayer();
        measureContrastTitles();

        if (interactionMode === 'trail') {
          scheduleTrailPositionUpdate();
        } else {
          repositionActiveLayer();
        }
      });

      resizeObserver.observe(mediaLayer);

      if (rowsContainer) {
        resizeObserver.observe(rowsContainer);
      }
    }

    measureLayer();
    measureContrastTitles();
    initMobileMediaLoading();
  });
};

initExpertiseSection();

window.initExpertiseSection = initExpertiseSection;
